import Tabs from "@/v2-backoffice-client/components/tabs"
import type { CustomerFragment } from "@/v2-backoffice-client/customer/__types__/customer.fragment"
import CustomerCompanyInsight from "@/v2-backoffice-client/customer/insight/CustomerCompanyInsight"
import { CUSTOMER_ACTIVITY_TABS_ID } from "@/v2-backoffice-client/customer/customer.links"
import UsageReportSection from "@/v2-backoffice-client/usage/report/UsageReportSection"
import UsageFusionReport from "@/v2-backoffice-client/usage/scope/UsageFusionReport"
import UsageScopeOverview from "@/v2-backoffice-client/usage/scope/UsageScopeOverview"
import UsageLicenseDeniedSummary from "@/v2-backoffice-client/usage/denied/UsageLicenseDeniedSummary"
import UsageMcpReport from "@/v2-backoffice-client/usage/mcp/UsageMcpReport"
import { useUsageMcpCustomerQuery } from "@/v2-backoffice-client/usage/mcp/usage.mcpCustomer.query"
import { MCP_HISTORY_DAYS } from "@/v2-backoffice-client/usage/mcp/usage.mcp.constants"
import { hasMcpActivity } from "@/v2-backoffice-client/usage/mcp/usage.mcp.utils"
import { APP_CODE_LABELS } from "@/v2-backoffice-client/usage/usage.constants"
import UserLoginPanel from "@/v2-backoffice-client/userLogin/list/UserLoginPanel"

export type CustomerActivityProps = {
  customer: CustomerFragment
  /** The customer's 12-month billed revenue, for the churn read. */
  revenueAtStake: { amount: number; currency: string | null } | null
}

// The customer's Activity tab: one overview, then one page per product.
// The Overview carries the company context, the cross-product key figures
// and story, what they asked for and do not have, and the recent sign-ins;
// Fusion, Placepoint MCP and Dataset MCP go deep on each product. The
// Dataset MCP page only appears once somebody at the customer has used it —
// most have not, and an always-empty tab is noise.
function CustomerActivity(props: CustomerActivityProps) {
  const { customer, revenueAtStake } = props
  const subject = customer.Name ?? "the customer"

  const datasetQuery = useUsageMcpCustomerQuery({
    variables: {
      customerId: customer.Id,
      days: MCP_HISTORY_DAYS,
      appCode: "mcp-dataset",
    },
  })
  const hasDataset = hasMcpActivity(
    datasetQuery.data?.getUsageMcpByCustomer.Days ?? [],
  )

  return (
    <Tabs
      id={CUSTOMER_ACTIVITY_TABS_ID}
      defaultValue="overview"
      className="flex flex-col gap-6"
    >
      <Tabs.List className="self-start">
        <Tabs.Trigger value="overview">Overview</Tabs.Trigger>
        <Tabs.Trigger value="fusion">Fusion</Tabs.Trigger>
        <Tabs.Trigger value="placepoint-mcp">
          {APP_CODE_LABELS["mcp-placepoint"]}
        </Tabs.Trigger>
        {hasDataset && (
          <Tabs.Trigger value="dataset-mcp">
            {APP_CODE_LABELS["mcp-dataset"]}
          </Tabs.Trigger>
        )}
      </Tabs.List>

      <Tabs.Content value="overview">
        <div className="usage-report">
          <UsageScopeOverview
            customerId={customer.Id}
            subject={subject}
            revenueAtStake={revenueAtStake}
          />
          <CustomerCompanyInsight customer={customer} />
          <UsageLicenseDeniedSummary
            customerId={customer.Id}
            subject={subject}
          />
          <UsageReportSection
            eyebrow="Sign-ins"
            title="Who has signed in recently"
            lead="Active Fusion sessions and recent sign-ins of the customer's people, with where they came from."
          >
            {/* The panel brings its own frame and toolbar; no card around it. */}
            <UserLoginPanel
              customerId={customer.Id}
              tableId="customer-activity-logins-table"
            />
          </UsageReportSection>
        </div>
      </Tabs.Content>

      <Tabs.Content value="fusion">
        <div className="usage-report">
          <UsageFusionReport customerId={customer.Id} subject={subject} />
        </div>
      </Tabs.Content>

      <Tabs.Content value="placepoint-mcp">
        <div className="usage-report">
          <UsageMcpReport
            customerId={customer.Id}
            appCode="mcp-placepoint"
            productName={APP_CODE_LABELS["mcp-placepoint"]}
            subject={subject}
          />
        </div>
      </Tabs.Content>

      {hasDataset && (
        <Tabs.Content value="dataset-mcp">
          <div className="usage-report">
            <UsageMcpReport
              customerId={customer.Id}
              appCode="mcp-dataset"
              productName={APP_CODE_LABELS["mcp-dataset"]}
              subject={subject}
            />
          </div>
        </Tabs.Content>
      )}
    </Tabs>
  )
}

export default CustomerActivity
